import React, { useRef, useLayoutEffect, useContext, useState } from 'react'
import PropTypes from 'prop-types'
import styled, { useTheme } from 'styled-components'
import { useMutation, useQueryClient } from 'react-query'
import { useDispatch } from 'react-redux'
import { MinimalRichEditorWithLoom } from '../../../mini/DraftEditors'
import { ReactTooltipStyled } from '../../../styled/typography/typographyExtentions'
import { EditorState } from 'draft-js'
import {
  contentFromHTMLWithVideo,
  contentToHTMLWithVideo
} from '../../../mini/DraftEditors/videoSeralization'
import _ParagraphIcon from '../../../../static/images/icons/paragraph_icon.svg'
import Checkbox from '../../../mini/Checkbox'
import withApiData from '../../../HOC/withApiData'
import Button from '../../../mini/Button'
import FlexAlignCenter from '../../../styled/FlexAlignCenter'
import { hasText, isEmptyListInEditor } from '../../../../utils/draftjs'
import { EditorWrapper, CancelButton, EditGuidance } from './styles'
import { mutateMentorGuidance } from './API'
import { MentorGuidanceContext } from './Index'
import RecordButton from '../../../mini/Loom/RecordButton'
import { clearEditorRecoveryData } from '../../../../actions/loom'
import { LoomContext } from '../../../../containers/LoomProvider'
import { H5, P2, Caption4 } from '../../../styled/typography/typography'
import useLexicon from '../../../../hooks/useLexicon'

const ParagraphIcon = styled.img.attrs({
  src: _ParagraphIcon
})`
  width: 9px;
  height: 11px;
`

const Edit = ({
  apiData,
  type,
  guidance,
  onCancel,
  onSave,
  loomData,
  recoveryAction,
  isJourney,
  isEditingJourneyGuidance
}) => {
  const mentorGuidanceContext = useContext(MentorGuidanceContext)
  const { enabled: isLoomRecordingEnabled } = useContext(LoomContext)

  const editor = useRef(null)

  const initialEditorState = () => {
    const initialContent =
      loomData && loomData.recovery
        ? loomData.recovery.content
        : guidance
        ? guidance.text
        : null
    if (initialContent) {
      return EditorState.createWithContent(
        contentFromHTMLWithVideo(initialContent)
      )
    } else {
      return EditorState.createEmpty()
    }
  }

  const [editorState, setEditorState] = useState(initialEditorState)
  const [focus, setFocus] = useState(false)
  const [applyToAllSections, setApplyToAllSections] = useState(true)
  const [initialized, setInitialized] = useState(false)
  const dispatch = useDispatch()
  const lexicon = useLexicon()
  const theme = useTheme()

  const queryClient = useQueryClient()

  const mutation = useMutation(({ applyToAllSections, guidanceText }) =>
    mutateMentorGuidance(
      apiData,
      mentorGuidanceContext,
      type,
      guidance,
      applyToAllSections,
      guidanceText
    )
  )

  useLayoutEffect(() => {
    setTimeout(() => {
      // focusText()
      setInitialized(true)
    }, 300)
  }, [])

  const focusText = () => {
    editor.current.focus()
  }

  const isSaveDisabled = !hasText(editorState)

  const getGuidance = () => {
    const content = editorState.getCurrentContent()
    return content.hasText()
      ? contentToHTMLWithVideo(editorState.getCurrentContent())
      : null
  }

  const onSaveClick = !isSaveDisabled
    ? () => {
        if (isJourney) {
          isEditingJourneyGuidance(false)
        }
        mutation.mutate(
          {
            applyToAllSections,
            guidanceText: getGuidance()
          },
          {
            onSuccess: () => {
              onSave()
              queryClient.invalidateQueries('journey-mentor-guidance')
              queryClient.invalidateQueries('level-mentor-guidance')
            }
          }
        )
      }
    : null

  const onCancelClick = () => {
    if (isJourney) {
      isEditingJourneyGuidance(false)
    }
    if (loomData && loomData.active) {
      dispatch(clearEditorRecoveryData())
      setEditorState(
        guidance
          ? EditorState.createWithContent(
              contentFromHTMLWithVideo(guidance.text)
            )
          : EditorState.createEmpty()
      )
    } else {
      onCancel()
    }
  }

  const isEmpty = !hasText(editorState) && isEmptyListInEditor(editorState)

  return (
    <EditGuidance isJourney={isJourney}>
      <div style={{ marginBottom: '15px' }}>
        <H5 as="h5" victoria>
          {lexicon.mentor_guidance}
        </H5>
      </div>
      <EditorWrapper focus={focus} onClick={focusText}>
        <P2
          className={`note-container ${
            isEmptyListInEditor(editorState) ? 'empty-list' : ''
          }`}
        >
          <MinimalRichEditorWithLoom
            ref={editor}
            className="note-description-editor"
            editorState={editorState}
            onChange={es => {
              setEditorState(es)
              if (isJourney) {
                isEditingJourneyGuidance(es.getCurrentContent().hasText())
              }
            }}
            onBlur={() => {
              setFocus(false)
            }}
            onFocus={() => {
              setFocus(true)
            }}
            placeholder={
              isEmpty
                ? ''
                : `Provide supplemental instruction and materials to students in addition to the ${lexicon.course.toLowerCase()} curriculum...`
            }
            loomData={loomData}
            initialized={initialized}
            sessionId={mentorGuidanceContext.sessionId}
            unsavedWorkId={mentorGuidanceContext.sessionId}
            isInModal={mentorGuidanceContext.isInModal}
          />
        </P2>

        {isLoomRecordingEnabled && (
          <FlexAlignCenter
            style={{ position: 'absolute', left: '22px', bottom: '10px' }}
          >
            <RecordButton
              text="+ Insert a Recording"
              sessionId={mentorGuidanceContext.sessionId}
              recoveryPath={mentorGuidanceContext.recoveryPath}
              recoveryAction={recoveryAction}
            />
          </FlexAlignCenter>
        )}

        <FlexAlignCenter
          style={{ position: 'absolute', right: '10px', bottom: '10px' }}
        >
          <ParagraphIcon
            style={{ marginRight: '7px' }}
            data-tip="Highlight text to see formatting options."
            data-for="paragraph-icon"
          />

          <ReactTooltipStyled id="paragraph-icon" effect="solid" />
        </FlexAlignCenter>
      </EditorWrapper>

      <FlexAlignCenter
        style={{
          justifyContent: 'space-between',
          width: '100%',
          marginTop: '12px'
        }}
      >
        <FlexAlignCenter>
          <Checkbox
            checked={applyToAllSections}
            onChange={e => {
              setApplyToAllSections(e.target.checked)
            }}
          />
          <Caption4 as="caption">
            Apply to all my {lexicon.section.toLowerCase()}s.
          </Caption4>
        </FlexAlignCenter>
        <FlexAlignCenter style={{ justifyContent: 'flex-end' }}>
          <CancelButton onClick={onCancelClick}>Cancel</CancelButton>
          <Button
            className={`quest-btn btn-a btn-a-${theme?.style} small ${
              !hasText(editorState) ? 'disabled' : ''
            }`}
            style={{ margin: 0 }}
            text="Save"
            onClick={onSaveClick}
          />
        </FlexAlignCenter>
      </FlexAlignCenter>
    </EditGuidance>
  )
}

Edit.propTypes = {
  apiData: PropTypes.object.isRequired,
  guidance: PropTypes.object.isRequired,
  type: PropTypes.number.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired
}

export default withApiData(Edit)
